// File: frontend/src/components/legal/LegalDocument.js
//
// Shared renderer for PesaFii's public legal documents (/privacy, /terms).
//
// This is the layout that used to live inside pages/Privacy.js. When the Terms
// of Service were added the choice was to copy that layout or to extract it;
// copying would have let two public legal pages drift apart in typography,
// print behaviour and the section index, which is exactly the kind of
// inconsistency a reader notices on a legal page. So it lives here once and
// both pages are thin wrappers that supply content.
//
// Content comes from the per-document source of truth in src/content
// (privacyPolicy.js, termsOfService.js), which also generates the committed
// markdown under docs/legal/. This component only presents it: PublicLayout
// shell (marketing header/footer + SEO meta), a jump-to-section index, and
// print-friendly long-form typography built from theme tokens (the lint config
// bans raw hex in pages).

import React from "react";
import { Box, Container, Typography, Divider } from "@mui/material";
import PublicLayout from "../marketing/PublicLayout";

const BODY_SX = { fontSize: 15, lineHeight: 1.75, color: "text.secondary" };

function Block({ block }) {
  if (block.h3) {
    return (
      <Typography component="h3" sx={{ mt: 2.5, mb: 0.5, fontSize: 16, fontWeight: 700, color: "text.primary" }}>
        {block.h3}
      </Typography>
    );
  }
  if (block.ul) {
    return (
      <Box component="ul" sx={{ my: 1, pl: 3 }}>
        {block.ul.map((item) => (
          <Typography key={item.slice(0, 40)} component="li" sx={{ ...BODY_SX, mb: 0.75 }}>
            {item}
          </Typography>
        ))}
      </Box>
    );
  }
  return <Typography sx={{ ...BODY_SX, my: 1.25 }}>{block.p}</Typography>;
}

/**
 * @param {object}   props
 * @param {string}   props.metaTitle        <title> / SEO title
 * @param {string}   props.metaDescription  SEO description
 * @param {string}   props.title            H1 of the document
 * @param {string}   props.version
 * @param {string}   props.effectiveDate
 * @param {string}   props.lastUpdated
 * @param {string}   props.indexAriaLabel   aria-label for the section index nav
 * @param {string}   props.indexHeading     visible heading above the section index
 * @param {Array}    props.sections         [{ id, title, blocks }]
 */
export default function LegalDocument({
  metaTitle,
  metaDescription,
  title,
  version,
  effectiveDate,
  lastUpdated,
  indexAriaLabel,
  indexHeading,
  sections,
}) {
  return (
    <PublicLayout title={metaTitle} description={metaDescription}>
      {/* Keep the print view readable: hide chrome, keep the document. */}
      <style>{`@media print { header, footer { display: none !important; } a[href]::after { content: ""; } }`}</style>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 }, minWidth: 0 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, letterSpacing: "-0.02em", color: "text.primary" }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: 13.5, color: "text.secondary" }}>
          Version {version}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          Effective date: {effectiveDate}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          Last updated: {lastUpdated}
        </Typography>

        {/* Section index */}
        <Box component="nav" aria-label={indexAriaLabel} sx={{ mt: 3, p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.disabled", mb: 1 }}>
            {indexHeading}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 3, columnCount: { xs: 1, sm: 2 }, columnGap: 4 }}>
            {sections.map((s) => (
              <Typography key={s.id} component="li" sx={{ fontSize: 13.5, mb: 0.5, breakInside: "avoid" }}>
                <Box
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    color: "text.secondary", textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                    "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  {s.title}
                </Box>
              </Typography>
            ))}
          </Box>
        </Box>

        {sections.map((s, i) => (
          <Box key={s.id} component="section" id={s.id} sx={{ scrollMarginTop: 96 }}>
            <Divider sx={{ mt: 4, mb: 3, borderColor: "divider" }} />
            <Typography component="h2" sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800, letterSpacing: "-0.01em", color: "text.primary" }}>
              {i + 1}. {s.title}
            </Typography>
            {s.blocks.map((b, j) => (
              <Block key={`${s.id}-${j}`} block={b} />
            ))}
          </Box>
        ))}
      </Container>
    </PublicLayout>
  );
}
